import { useState, type ReactNode } from 'react';
import { rozhrania, type ExterneRozhranie } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { Badge, Button, Card, Notice, PageHeader, SectionTitle } from '../components/ui.js';
import { IconCheck, IconExternal, IconRefresh } from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data } from './common.js';

/*
 * Rozhrania vedlajsich sluzieb.
 *
 * Zigbee2MQTT a border router maju vlastne webove rozhrania a na
 * diagnostiku su uzitocne: vidno v nich veci, ktore LoxLink nerobi -
 * parovanie cez Touchlink, topologiu z pohladu mosta, podrobnosti OTA.
 *
 * Brana ich NEPROXUJE a je to zamerne. Keby ich servirovala pod vlastnou
 * adresou, dostalo by sa cudzie admin rozhranie na nas origin a jeho
 * JavaScript by videl nasu session cookie. Dnes ich od nas oddeluje port
 * a tu hranicu nemazeme kvoli pohodliu. Odtialto sa teda da preklikat,
 * ale spojenie ide tunelom.
 */

interface PopisRozhrania {
  readonly nazov: string;
  readonly popis: string;
  /** Na co je dobre - teda preco by tam clovek chodil. */
  readonly naCo: readonly string[];
  /** Co robit, ked nebezi. */
  readonly keNebezi: ReactNode;
}

const POPISY: Record<ExterneRozhranie['id'], PopisRozhrania> = {
  zigbee2mqtt: {
    nazov: 'Zigbee2MQTT',
    popis: 'Most, ktorý obsluhuje celú Zigbee sieť. Beží vedľa brány vo vlastnom kontejneri.',
    naCo: [
      'párovanie cez Touchlink a párovanie podľa výrobcu',
      'topológia siete z pohľadu mosta, s vysvetlením väzieb',
      'podrobnosti o OTA aktualizáciách a ich priebehu',
      'skupiny, scény a nastavenia jednotlivých zariadení',
    ],
    keNebezi: (
      <>
        Most nebeží. Skontroluj ho príkazom <code>docker compose logs zigbee2mqtt</code> — bez neho
        nefunguje ani celá Zigbee sieť, nie len toto rozhranie.
      </>
    ),
  },
  otbr: {
    nazov: 'OpenThread Border Router',
    popis: 'Vlastné rozhranie border routera. Zakladanie siete, dataset a pripájanie zariadení.',
    naCo: [
      'topológia Thread siete a stav jednotlivých uzlov',
      'commissioner — pripájanie Thread zariadení (to v LoxLinku zatiaľ nie je)',
      'úplný dataset siete vrátane položiek, ktoré brána zámerne nečíta',
    ],
    keNebezi: (
      <>
        Rozhranie border routera je <strong>predvolene vypnuté</strong>. Zapneš ho tak, že do{' '}
        <code>deploy/.env</code> dáš <code>OT_WEB_ENABLED=1</code> a spustíš{' '}
        <code>docker compose up -d otbr</code>. Ak obraz pochádza z verzie pred 0.103.0, treba ho
        najprv zostaviť znova: <code>docker compose build otbr</code>.
      </>
    ),
  },
};

export function Rozhrania(): ReactNode {
  const nacitanie = useApi((signal) => rozhrania.zoznam(signal), []);
  const { user } = useSession();

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Rozhrania" detail="Rozhrania služieb, ktoré bežia vedľa brány." />
        <Notice tone="warn">Táto obrazovka je len pre správcu.</Notice>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Rozhrania"
        detail="Vlastné rozhrania služieb, ktoré bežia vedľa brány. Na diagnostiku a na to, čo LoxLink nerobí."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />

      <Card className="flex flex-col gap-1 p-4 sm:p-5">
        <p className="text-sm font-medium text-(--color-text)">
          Prečo cez tunel a nie priamo v LoxLinku
        </p>
        <p className="max-w-prose text-sm text-(--color-text-muted)">
          Obe rozhrania sedia zámerne len na slučke brány a LoxLink ich neproxuje. Keby ich
          servíroval pod vlastnou adresou, cudzie správcovské rozhranie by sa dostalo na jeho origin
          a jeho JavaScript by videl prihlasovaciu cookie. Dnes ich oddeľuje port — a tú hranicu
          nemažeme kvôli pohodliu. Otvor si tunel a odkaz nižšie potom funguje.
        </p>
      </Card>

      <Data nacitanie={nacitanie}>
        {(data) => (
          <div className="flex min-w-0 flex-col gap-4">
            {data.rozhrania.map((r) => (
              <KartaRozhrania key={r.id} rozhranie={r} />
            ))}
          </div>
        )}
      </Data>
    </div>
  );
}

function KartaRozhrania({ rozhranie: r }: { readonly rozhranie: ExterneRozhranie }): ReactNode {
  const popis = POPISY[r.id];
  const [skopirovane, setSkopirovane] = useState(false);

  const skopiruj = (): void => {
    void navigator.clipboard
      .writeText(r.tunel)
      .then(() => {
        setSkopirovane(true);
        setTimeout(() => setSkopirovane(false), 2000);
      })
      .catch(() => {
        // Prehliadac moze kopirovanie odmietnut; prikaz je aj tak vypisany.
      });
  };

  return (
    <Card className="flex min-w-0 flex-col gap-4 p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <SectionTitle>{popis.nazov}</SectionTitle>
          <p className="mt-1 max-w-prose text-sm text-(--color-text-muted)">{popis.popis}</p>
        </div>
        <Badge tone={r.bezi ? 'ok' : 'neutral'}>{r.bezi ? 'Beží' : 'Nebeží'}</Badge>
      </div>

      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-sm font-medium text-(--color-text)">Načo je to dobré</p>
        <ul className="list-inside list-disc text-sm text-(--color-text-muted)">
          {popis.naCo.map((veta) => (
            <li key={veta} className="break-words">
              {veta}
            </li>
          ))}
        </ul>
      </div>

      {r.bezi ? (
        <>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-sm font-medium text-(--color-text)">1. Otvor si tunel</p>
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <code className="min-w-0 flex-1 overflow-x-auto rounded-(--radius-control) bg-(--color-surface-sunken) px-3 py-2 text-xs whitespace-pre">
                {r.tunel}
              </code>
              <Button
                variant="ghost"
                onClick={skopiruj}
                icon={skopirovane ? <IconCheck className="size-4" /> : undefined}
              >
                {skopirovane ? 'Skopírované' : 'Kopírovať'}
              </Button>
            </div>
          </div>

          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-sm font-medium text-(--color-text)">2. Otvor rozhranie</p>
            <div className="flex flex-wrap items-center gap-2">
              {/*
               * `noreferrer` zamerne: cudzie rozhranie nema dostat adresu,
               * z ktorej sa na nu kliklo.
               */}
              <a
                href={r.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-(--radius-control) bg-(--color-accent) px-3 py-2 text-sm font-medium text-(--color-accent-text)"
              >
                <IconExternal className="size-4" />
                {r.url}
              </a>
              <span className="text-xs text-(--color-text-subtle)">
                otvorí sa v novom okne; funguje, keď beží tunel
              </span>
            </div>
          </div>
        </>
      ) : (
        <Notice tone="warn">{popis.keNebezi}</Notice>
      )}
    </Card>
  );
}
